// Copyright 2019 Sorama B.V.

import { containerless, customElement } from "aurelia-framework";
import { DivAspectEvent } from "controls/aspect/DivAspect";
import { AspectItemsCatalog } from "./AspectItemsCatalog";
import { AspectItemRatio, IAspectItem } from "./IAspectItem";

export class AspectItemElement {
    public element: HTMLElement;
    public viewModel: IAspectItem;
}

@containerless
@customElement("aspect-group-container")
export class AspectGroupContainer {
    private contentElement: HTMLDivElement;

    private mutationObserver: MutationObserver;

    private finalRatio = 1;

    constructor(private aspectItemContainer: AspectItemsCatalog) {}

    public attached(): void {
        if (this.contentElement) {
            this.mutationObserver = new MutationObserver(() => {
                this.sizeChanged({
                    externalWidth: this.contentElement.clientWidth,
                    externalHeight: this.contentElement.clientHeight,
                });
            });

            this.mutationObserver.observe(this.contentElement, {
                childList: true,
                subtree: true,
            });
        }
    }

    public detached(): void {
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
    }

    public sizeChanged(event: DivAspectEvent): void {
        const childList = this.getAspectDivChilds();

        if (!childList || childList.length == 0) return;

        const nrOfElements = childList.length;

        let maxArea = 0;
        let nrOfRows = 0;

        // Assumption: all elements have the same aspect ratio.
        for (let i = 1; i <= nrOfElements; i++) {
            const nrOfHorElements = Math.ceil(nrOfElements / i);

            const maxHeight = Math.floor(event.externalHeight / i);
            const maxWidth = Math.floor(event.externalWidth / nrOfHorElements);

            let result: AspectItemRatio;
            let area = 0;

            for (let j = 0; j < childList.length; j++) {
                const tempResult = childList[j].viewModel.getArea(maxWidth, maxHeight);
                const tempArea = tempResult.height * tempResult.width;

                if (tempArea > area) {
                    area = tempArea;
                    result = tempResult;
                }
            }

            if (area > maxArea) {
                maxArea = area;
                nrOfRows = i;
                this.finalRatio = (nrOfHorElements * result.width) / (i * result.height);
            }
        }

        const nrOfHorElements = Math.ceil(nrOfElements / nrOfRows);

        const maxWidth = (1 / nrOfHorElements) * 100 + "%";

        for (const child of childList) {
            child.element.style.maxWidth = maxWidth;
        }
    }

    public getAspectDivChilds(): AspectItemElement[] {
        if (!this.contentElement) return [];

        const result: AspectItemElement[] = [];
        const element = this.contentElement;

        for (let i = 0; i < element.children.length; i++) {
            const child: Element = element.children[i];

            const item = this.aspectItemContainer.getItem(child);
            if (item) {
                result.push(item);
            }
        }

        return result;
    }
}
